// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import { withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../../../styles/import.css';

import api from '../../../api';

import ImportFileProvider from './FileProvider';

const SCRIPT_TEMPLATE = "# The Box integration will run the function below which needs to scan for\n# appropriate files in the given folder and return the list of relative paths\n# from the folder to upload.\n\n# If the file exists in Stax.ai and has not been modified, it will not be\n# uploaded even if you return it from the function.\n\ndef find_files_to_upload(folder):\n    files = []\n    for file in os.listdir(folder):\n        files.append(os.path.join(folder, file))\n    \n    return files \n";

class ImportBox extends ImportFileProvider {
    // State from ImportFileProvider 
    // state = {
    //     authenticated, 
    //     configurations,
    //     config, 
    //     edit, 
    //     oauth, 
    //     folders, 
    //     expanded,
    //     stacks,
    //     template,
    // }

    scriptTemplate = SCRIPT_TEMPLATE;

    loadConfig =async () => {
        // loading all the existing stacks for this team
        const res = await api.get('/stack/all?minimal=true');
        if (res && res.success && res.stacks)
            this.setState({ stacks: res.stacks });
        else return toast.error("Failed to load stacks list");
        const { connectionIdx } = this.props.data; 
        const config = this.props.team.sync?.box?.[connectionIdx] || {
            oauth: '',
            configurations: [],
            authenticated: false, 
        }

        this.setState({
            ...config, 
            configurations: config.folders || [],
            authenticated: false, 
            scriptTemplate: SCRIPT_TEMPLATE, 
        });
    }

    saveConfig = async () => {
        const sync = this.props.team?.sync?? {}; 
        const connections = sync?.box??[];
        const connectionIdx = this.props.data?.connectionIdx ?? connections.length; 
        
        const connection = {
            user: this.props.user._id, 
            oauth: this.state.oauth,
            ...(connections[connectionIdx] || {}),
            folders: this.state.configurations,
            moveUploadedFiles: this.state.moveUploadedFiles,
        };

        if(connections.length === 0)
            connections.push(connection);
        else
            connections[connectionIdx] = connection; 

        // Create stacks for all selected ...
        for(let folder of connection.folders)   {

            // Check if the folder already exists in the team
            const existingStack = this.state.stacks.find((stack) => stack.path === folder.name);

            // If the folder exists, skip creating a new stack
            if(existingStack) {
                continue;
            }

            const res = await api.post('/stack/create', {stackPath: folder.name});

            if (!res || !res.stack) {
                toast.error("Failed to create a stack for the folder: " + folder.source);
                return this.props.loaded();
            } else {
                const destination = res.stack._id;
                // Update the folder with the stack id
                folder.destination = destination;
                // Update the folder in the connection
                connection.folders = connection.folders.map((f) => {
                    if(f.name === folder.name) {
                        return folder;
                    }
                    return f;
                });
            }
            this.setState({ stacks: [...this.state.stacks, res.stack] });
        }
        // Save the connection
        sync.box = connections;

        await this.props.updateTeam({sync});

        // Force a manual sync now
        const res = await api.get('/sync/connections?source=box');
        if (res && res.connections && res.connections.length > 0) {
            await api.post('/sync/manual', {
                connectionId: res.connections[connectionIdx > 0 ? connectionIdx : connections.length - 1]._id,
                source: 'box',
            });
        
        toast.info("Your Box files will start syncing shortly");   
        this.props.loaded();
        this.props.modal();
    }
}

    authenticate = async () => {
        // Do we already have the oauth token? 
        const { code } = this.props.data;

        let oauthId = this.state.oauth;
        if(this.state.oauth){
            const res = await api.get(`/sync/box/refresh?oauth=${this.state.oauth}`);
            if(!res?.success??false)
                return toast.error("Unable to refresh the Box connection");
        }else{
            // Handle authentication... 
            const res = await api.post("/sync/box/token", { code });  
            if (!res || !res.oauthId)
                return toast.error("Unable to get a secure token from the Box API");
            oauthId = res.oauthId;
        }

        // Save the oauth token... 
        this.setState({ oauth: oauthId, authenticated: true });
    }

    // // Authenticate the connection with Box and load root
    loadRoot = async () => {
        this.setState({ loading: true });
        const {folders} = await this.listFolders();
        this.setState({
            folders,
            loading: false
        });
    }

  
    listFolders = async (folderId) => {
        const folders = []; 
        const entries = []; 
        
        const res = await api.post("/sync/box/list", {
            oauth: this.state.oauth,
            ...(folderId? { folderId } : {})
        });

        if (!res || !res.success){
            toast.error("Unable to list folders from Box");
            return { folders: [] };
        }

        if (res && res.success && res.connection && res.connection.moveUploadedFiles !== undefined){
            this.setState({ moveUploadedFiles: res.connection.moveUploadedFiles });
        }

        entries.push(...res.entries);

        for(const entry of entries){
            if(entry.type !== "folder")
                continue; 

            folders.push({
                name: entry.name,
                path: entry.path,
                path_display: entry.path,
                shared: false,
                _id: entry.id,
                numDocuments: 0,
                loadChildren: async() => {
                    const children = await this.listFolders(entry.id);
                    this.loading = false;
                    this.loaded = true; 
                    this.setState({folders: [...this.state.folders, ...children.folders]});
                }
            });
        };

        return { folders }
    }


    // Save configuration for box connection
    renderDescription = () => {
        return(
            <div>
                <h1>Sync documents from Box</h1>
                <p>
                    Stax·ai will make stacks for each Box folder you select below and mirror the contents of these folders into your new stacks.
                </p>
            </div>
        )
    }

    renderContent = () => {
        return <div>{this.renderConfigEditor()}</div>
    }
}


export default withRouter(ImportBox);